import { useCallback, useEffect, useRef, useState } from 'react';

import URLS from 'constants/urls';
import { transformKeysToCamelCase } from 'helpers/core';
import { httpClient } from 'helpers/http';

import { PAGE_SIZE, TRANSACTION_KIND } from './SkuPayoutReports.constants';

const useSkuPayoutReportDetail = ({ venueId, payoutId }) => {
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const fetchTransactions = useCallback(
    async uiPage => {
      const { data } = await httpClient.get(
        URLS.reports.transactions({
          transaction_type: TRANSACTION_KIND.ORDER,
          payout_id: payoutId,
          venue_id: venueId,
          page: Math.max(0, uiPage - 1),
          per_page: PAGE_SIZE,
        })
      );
      return transformKeysToCamelCase(data);
    },
    [payoutId, venueId]
  );

  const fetchSummary = useCallback(async () => {
    const { data } = await httpClient.get(URLS.reports.payoutDetails({ payoutId, venueId }));
    return transformKeysToCamelCase(data);
  }, [payoutId, venueId]);

  useEffect(() => {
    if (venueId == null || payoutId == null) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setPage(1);

    const load = async () => {
      try {
        const [summaryData, transactionsData] = await Promise.all([fetchSummary(), fetchTransactions(1)]);
        if (cancelled) return;
        setSummary(summaryData);
        setTransactions(transactionsData?.transactions || []);
        setTotalItems(transactionsData?.totalItems || 0);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [venueId, payoutId, fetchSummary, fetchTransactions]);

  const changePage = useCallback(
    async nextPage => {
      if (venueId == null || payoutId == null) return;
      requestIdRef.current += 1;
      const id = requestIdRef.current;
      setIsLoading(true);
      setPage(nextPage);
      try {
        const transactionsData = await fetchTransactions(nextPage);
        if (id !== requestIdRef.current) return;
        setTransactions(transactionsData?.transactions || []);
        setTotalItems(transactionsData?.totalItems || 0);
      } catch (err) {
        if (id === requestIdRef.current) setError(err);
      } finally {
        if (id === requestIdRef.current) setIsLoading(false);
      }
    },
    [venueId, payoutId, fetchTransactions]
  );

  return { summary, transactions, totalItems, page, setPage: changePage, isLoading, error };
};

export default useSkuPayoutReportDetail;
